import { useState } from 'react';
import { Link } from 'react-router';
import type { CategoryTileViewModel } from '@rocco/ui/tile-category/view-models/category';
import { cn } from '@rocco/utils/cn';
import { useDirectoryBrowseTheme } from '~/ui-rocco/pages/directory-browse/directory-browse-theme';

interface CategoryGroupProps {
    category: CategoryTileViewModel;
    isDark: boolean;
}

/**
 * Below `md`, each group collapses behind its heading row (closed by default)
 * so the sitemap doesn't read as one long stacked wall on mobile; the chevron
 * button toggles it. At `md` and above the sub-list is always shown. The
 * collapse is CSS-only (`max-md:hidden`) over React state — NOT a native
 * `<details>`: a closed `<details>` hides its content at the browser level on
 * every breakpoint, and no stylesheet can force it open cross-browser. Every
 * link stays in the DOM in both states, so crawlers always see the anchors.
 */
const CategoryGroup = ({ category, isDark }: CategoryGroupProps) => {
    const [expanded, setExpanded] = useState(false);
    const headingLink = (
        <Link
            to={category.link}
            className={cn(
                'text-4 leading-[1.3] font-semibold uppercase transition-colors',
                isDark
                    ? 'text-offwhite hover:text-offwhite/70'
                    : 'text-[#323232] hover:text-[#4B4B4B]',
            )}
        >
            {category.title}
        </Link>
    );

    // "View all" sits a clear row below the sub-link rhythm — mt-6 (1.5rem ≈ 24px)
    // matches the production sitemap spacing.
    const viewAllLink = (
        <Link
            to={category.link}
            className={cn(
                'mt-6 text-3.5 font-normal transition-colors hover:underline max-md:flex max-md:min-h-8 max-md:items-center',
                isDark
                    ? 'text-offwhite/70 hover:text-offwhite'
                    : 'text-[#4B4B4B] hover:text-[#323232]',
            )}
            aria-label={`View all ${category.title} (${(category.itemsCount ?? 0).toLocaleString()})`}
        >
            View all ({(category.itemsCount ?? 0).toLocaleString()})
        </Link>
    );

    // Below md the groups stack as full-width accordion rows split by a hairline;
    // at md and above the wrappers are plain grid cells (no divider / row padding),
    // so the desktop multi-column sitemap is untouched.
    const rowDivider = cn(
        'max-md:border-b',
        isDark ? 'border-offwhite/15' : 'border-[#e5e5e5]',
    );

    // No sub-categories means there's nothing to disclose — render the plain
    // heading + view-all link (as a full-width row below md).
    if (category.subCategories.length === 0) {
        return (
            <div className={cn('flex flex-col max-md:py-3.5', rowDivider)}>
                {headingLink}
                {viewAllLink}
            </div>
        );
    }

    return (
        <div className={cn('flex flex-col', rowDivider)}>
            {/* One-line row below md: heading left, chevron right-aligned.
                min-h-11 keeps a comfortable ≥44px tap target; the heading link
                navigates while the chevron button discloses the sub-list. */}
            <div className="flex min-h-11 items-center justify-between gap-3 max-md:py-3 md:min-h-0">
                {headingLink}
                <button
                    type="button"
                    onClick={() => setExpanded((current) => !current)}
                    aria-expanded={expanded}
                    aria-label={`${expanded ? 'Collapse' : 'Expand'} ${category.title}`}
                    className={cn(
                        'flex size-11 shrink-0 items-center justify-center md:hidden',
                        isDark ? 'text-offwhite/70' : 'text-[#4B4B4B]',
                    )}
                >
                    <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        aria-hidden="true"
                        className={cn(
                            'size-5 transition-transform duration-150',
                            expanded && 'rotate-180',
                        )}
                    >
                        <path
                            d="M6 9l6 6 6-6"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </button>
            </div>

            {/* Always rendered (SEO surface — mobile-first indexing is where
                it earns its keep); collapsed below md purely with CSS. */}
            <div className={cn('max-md:pb-4', !expanded && 'max-md:hidden')}>
                <ul className="mt-3 flex flex-col gap-[5px]">
                    {category.subCategories.map((sub) => (
                        <li key={`${category.id}-${sub.id}`}>
                            <Link
                                to={sub.link}
                                className={cn(
                                    'text-3.5 leading-[112.5%] font-normal tracking-[-0.28px] transition-colors hover:underline max-md:flex max-md:min-h-8 max-md:items-center',
                                    isDark
                                        ? 'text-offwhite/70 hover:text-offwhite'
                                        : 'text-[#4B4B4B] hover:text-[#323232]',
                                )}
                            >
                                {sub.title}
                            </Link>
                        </li>
                    ))}
                </ul>
                {viewAllLink}
            </div>
        </div>
    );
};

interface ExploreAllCategoriesProps {
    categories: CategoryTileViewModel[];
    title?: string;
}

/**
 * Sitemap-style section displaying all categories in a multi-column grid.
 * Each category group shows its title, subcategory links, and a "View all (count)" link.
 *
 * Used across products and projects pages. Flips to light text on the dark
 * projects browse theme (via the directory browse theme context).
 */
export const ExploreAllCategories = ({
    categories,
    title = 'Explore all product categories',
}: ExploreAllCategoriesProps) => {
    const isDark = useDirectoryBrowseTheme() === 'dark';

    if (categories.length === 0) {
        return null;
    }

    return (
        <section className="mb-10 md:mb-35">
            <div className="flex flex-col gap-9 px-6 md:px-12">
                <p
                    className={cn(
                        'text-3.5 leading-[1.22] font-medium tracking-[-0.75px]',
                        isDark ? 'text-offwhite/50' : 'text-[#999]',
                    )}
                >
                    More to discover
                </p>
                <h2
                    className={cn(
                        // Below md the display heading scales down (40px @ 414) so
                        // it doesn't eat half the viewport; md+ sizing is unchanged.
                        // Tracking in em, not px: this heading has three sizes
                        // (2.5rem / 4rem / 5rem) and a fixed px value reads
                        // very differently at each. An em value holds the same
                        // tightness across all three.
                        'text-[4rem] leading-[0.9] font-medium tracking-[-0.045em] max-md:text-[2.5rem] max-md:leading-tight lg:text-[5rem]',
                        isDark ? 'text-offwhite' : 'text-[#323232]',
                    )}
                >
                    {title}
                </h2>
            </div>

            <hr
                className={cn(
                    // The wider run above the rule is a desktop measure; mobile
                    // keeps its original gap, and this block also renders on
                    // the pages the browse redesign does not cover.
                    'mx-6 mt-8 border-t md:mx-12 md:mt-24',
                    isDark ? 'border-offwhite/15' : 'border-[#e5e5e5]',
                )}
            />

            {/* Below md: single-column full-width accordion rows (hairline
                dividers, no grid gap). md+: the multi-column sitemap is unchanged.
                items-start: avoid row stretch so short columns don't sit in a tall
                cell (uneven gap above View all vs neighbours). */}
            <div className="mt-11 grid grid-cols-1 items-start gap-y-0 px-6 md:grid-cols-5 md:gap-x-8 md:gap-y-16 md:px-12 lg:grid-cols-6">
                {categories.map((category) => (
                    <CategoryGroup
                        key={category.id}
                        category={category}
                        isDark={isDark}
                    />
                ))}
            </div>
        </section>
    );
};
